import {Canvas, useFrame, useThree} from "@react-three/fiber";
import * as TWEEN from "@tweenjs/tween.js";
import _ from "lodash";
import {observer_mgl, RunInAction} from "mobx-graphlink";
import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {Row} from "react-vcomponents";
import * as THREE from "three";
import {GetMapObject, GetMapObjects} from "../../../Server/DB/mapObjects.js";
import {IsMapObjectHidden, ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../../Server/DB/mapObjects/@MapObject.js";
import {DMap} from "../../../Server/DB/maps/@Map.js";
import {MapState} from "../../../Store/main/maps/@MapState.js";
import {observer_mgl_r3} from "../../../Utils/ThreeJS/General.js";
import {Vec2, Vec3, Rect} from "../../../Utils/ThreeJS/VectorStructs.js";
import {CursorMarker} from "./MapSurface/CursorMarker.js";
import {MapControls, MapControls_React, MapControlsOptions} from "./MapSurface/MapControls.js";
import {MapObjectUI} from "./MapSurface/MapObjectUI.js";
import {MapObject3D, ObjHasRenderableBuilding} from "./MapSurface/MapObject3D.js";
import {PolygonGuide} from "./Toolbox/PolygonGuide.js";
import {store} from "../../../Store/index.js";
import {MapTool} from "./Toolbox/Tools/@Tool.js";
import {mapZIndexes} from "./MapSurface/MapZIndexes.js";
import {MapSurface_MenuStub} from "./MapSurface/MapSurface_Menu.js";
import {FocusAreaMarker} from "./MapSurface/FocusAreaMarker.js";
import {ViewpointOverlay} from "./MapSurface/ViewpointOverlay.js";
import {TerrainRenderer} from "../../../Terrain/Heights/TerrainRenderer.js";
import {resolveActiveViewpoint, resolveViewpointCamera} from "../../../Terrain/Heights/ViewpointCamera.js";
import {SceneryRenderer} from "../../../Terrain/Scenery/Render/SceneryRenderer.js";
import {IsolineSamplePointsOverlay} from "../../../Terrain/Heights/IsolineSamplePointsOverlay.js";
import {collectRenderStats, markRenderStart, markRenderEnd} from "../../../Utils/ThreeJS/MapObjectRenderStats.js";
import {Invalidate3JS} from "../../../Utils/ThreeJS/Invalidate3JS.js";
import {Map3D_SkyFog} from "./MapSurface/Map3D_SkyFog.js";
import {Map3D_SceneLights} from "./MapSurface/Map3D_SceneLights.js";
import {resolveShadowBias, resolveShadowMapSize, resolveShadowRadius, resolveShadowsEnabled} from "../../../Terrain/Heights/TerrainShadows.js";

export function MapSurface_Current() { return MapSurface_current; }
export let MapSurface_current: MapSurface_SelfState|n;

/** The stable per-map state object shared between `MapSurface`, its in-Canvas children, and the
 *  global `MapSurface_current` handle (which other UI exposes for focus requests). */
type MapSurface_SelfState = {
	mapControls: MapControls|n;
	canvas: ()=>HTMLCanvasElement|n;
	camera: THREE.OrthographicCamera;
	tweenGroup: TWEEN.Group;
	/** The R3F `invalidate` fn (installed by `TweenUpdater` on mount). Lets a freshly-started tween
	 *  "drive itself" in demand mode by requesting its own first frame. */
	invalidate: (()=>void)|n;
	FocusObject: (obj: MapObject)=>void;
	FocusWorldPos: (targetPos: Vec3)=>void;
};

export const MapSurface = observer_mgl((props: {map: DMap, mapState: MapState})=>{
	const {map, mapState} = props;
	const uiState = store.main.maps;
	const mapObjects = GetMapObjects(map.id);

	const canvasRef = React.useRef<HTMLCanvasElement>(null!);
	//const cameraRef = React.useRef<THREE.OrthographicCamera>(null!);

	const self = useMemo<MapSurface_SelfState>(()=>({
		mapControls: null as MapControls|n,
		canvas: (()=>null) as ()=>HTMLCanvasElement|n,
		invalidate: null as (()=>void)|n,
		//camera: (()=>null) as ()=>THREE.OrthographicCamera|n,
		camera: (()=>{
			const cam = new THREE.OrthographicCamera();
			cam.position.copy(mapState.camPos_500ms.NewZ(mapZIndexes.camera).To3JPos());
			//cam.zoom = mapState.camZoom;
			const ZoomLevel_Transformed = (zoomLevel: number, transformBase = 1.5)=>Math.pow(transformBase, zoomLevel); // mimic MapControls.ZoomLevel_Transformed
			cam.zoom = ZoomLevel_Transformed(mapState.camZoom_500ms);

			cam.updateProjectionMatrix();

			return cam;
		})(),
		tweenGroup: new TWEEN.Group(),
		FocusObject: (obj: MapObject)=>{
			self.FocusWorldPos(ResolveObjPosInWorld(obj));
		},
		FocusWorldPos: (targetPos: Vec3)=>{
			const mapControls = self.mapControls;
			if (!mapControls) { return; }
			const currentPosition = self.camera.position.clone();
			const tween = new TWEEN.Tween(currentPosition)
				.to(targetPos.To3JPos(), 1000)
				.easing(TWEEN.Easing.Quadratic.Out)
				.onUpdate(()=>{
					mapControls.panTo(Vec3.From3JPos(currentPosition).ToVec2());
					//mapControls.setZoom(?);
				})
				.start();
			self.tweenGroup.removeAll(); // clear existing tweens (only latest ever needs to be active)
			self.tweenGroup.add(tween);
			// Request a frame-render, so that if in on-demand render mode, we get the useFrame call-chain started
			// (TweenUpdater will keep it going until the tween completes, once we get it started)
			self.invalidate?.();
		},
	}), []);
	useEffect(()=>{
		MapSurface_current = self;
		return ()=>void(MapSurface_current = null);
	});
	Object.assign(self, {canvas: ()=>canvasRef.current /*, camera: ()=>cameraRef.current*/});

	/*const onDragStart = useCallback((drag: DragInfo)=>{
		if (drag.button != 0) return;
		if (uiState.tools.activeTool == MapTool.select) {
			drag.showRect = true;
		}
	}, [mapState]);
	const onDragComplete = useCallback((drag: DragInfo)=>{
		if (drag.button != 0) return;
		if (uiState.tools.activeTool == MapTool.select) {
			RunInAction("MapControls_React_onLeftDragComplete", ()=>{
				const min = new Vec2(Math.min(drag.startPos_world.x, drag.endPos_world.x), Math.min(drag.startPos_world.y, drag.endPos_world.y));
				const max = new Vec2(Math.max(drag.startPos_world.x, drag.endPos_world.x), Math.max(drag.startPos_world.y, drag.endPos_world.y));
				const rect = new Rect(min, max.Minus(min));
				const objectsInRect = mapObjects.filter(a=>{
					const objPosInWorld = ResolveObjPosInWorld(a);
					return rect.Intersects(new Rect(objPosInWorld.ToVec2(), Vec2.zero));
				}).map(a=>a.id);
				mapState.selectedObjects = objectsInRect;
				mapState.activeObject = objectsInRect[0];
			});
		}
	}, [mapState, mapObjects]);*/
	const onCamTransformChanged = useMemo(()=>{
		/*const throttledHandler_100ms = _.throttle((pos: Vec3, zoom: number)=>{
			RunInAction("MapControls_React_onCamTransformChanged_100ms", ()=>{
				mapState.camPos_100ms = pos;

				// temp
				//mapState.camPos_0ms = pos;
				//mapState.camZoom_0ms = zoom;
			});
		}, 100, {leading: true, trailing: true});*/
		const throttledHandler_500ms = _.throttle((pos: Vec3, zoom: number)=>{
			RunInAction("MapControls_React_onCamTransformChanged_500ms", ()=>{
				mapState.camPos_500ms = pos;
				mapState.camZoom_500ms = zoom;
			});
		}, 500, {leading: true, trailing: true});
		return (pos: Vec3, zoom: number)=>{
			RunInAction("MapControls_React_onCamTransformChanged", ()=>{
				mapState.camPos_0ms = pos;
				mapState.camZoom_0ms = zoom;
				//throttledHandler_100ms(pos, zoom);
				throttledHandler_500ms(pos, zoom);
			});
		};
 	}, [mapState]);
	const mapControlsOptions = useMemo(()=>{
		return new MapControlsOptions({
			/*onDragStart,
			onDragComplete,*/
			onCamTransformChanged,
		});
	}, [onCamTransformChanged]);

	// ── Set15 P1 — 3D mode (locked viewpoint) ─────────────────────────────────────────────
	// Activation is UI state (§5): `view3D_enabled` remounts the Canvas with a PerspectiveCamera
	// from the active `Mod_Viewpoint`, renders only the §5 allow-list (terrain + scenery + sky/fog),
	// and hard-gates tools/controls/overlays.  `view3D_activeViewpointObjID` selects the viewpoint.
	const view3D = mapState.settings.view3D;
	const view3DEnabled = view3D.view3D_enabled === true;
	// The viewpoint-bearing objects (for the hint text + any auto-selection).
	const viewpointObjects = mapObjects.filter(o=>(o.modifiers ?? []).some(m=>m.enabled && m.viewpoint));
	// The INITIAL 3D `<Canvas>` camera. Hold a STABLE instance in a ref, keyed on the camera's real
	// inputs, so R3F's per-render `configure` never sees a NEW camera reference (which would swap in
	// a fresh aspect-1 camera — the momentary aspect glitch). Mutate it in place when inputs change.
	const view3DCameraRef = useRef<{sig: string, cam: THREE.PerspectiveCamera|null}>({sig: "", cam: null});
	const view3DCamera = (()=>{
		if (!view3DEnabled) { view3DCameraRef.current.sig = ""; view3DCameraRef.current.cam = null; return null; }
		const vp = resolveActiveViewpoint(map.id, view3D.view3D_activeViewpointObjID);
		const vpMod = vp?.mod;
		const vpAnchor = vp ? ResolveObjPosInWorld(vp.obj) : null;
		const vpRot = vp ? ResolveObjRotInWorld(vp.obj) : null; // container's world rotation feeds the camera yaw
		const sig = [vpAnchor?.x, vpAnchor?.y, vpMod?.eyeHeight, (vpMod?.yaw ?? 0) + (vpRot ?? 0), vpMod?.pitch, vpMod?.fov, vpMod?.roll].join("|");
		const holder = view3DCameraRef.current;
		if (holder.sig == sig) return holder.cam; // unchanged → same instance (R3F won't swap)
		holder.sig = sig;
		if (!vp || !vpMod || !vpAnchor) { holder.cam = null; return null; }
		const desired = resolveViewpointCamera(map.id, view3D.view3D_activeViewpointObjID);
		if (!desired) { holder.cam = null; return null; }
		if (!holder.cam) { holder.cam = desired; return desired; }
		// Mutate the stable instance in place (never return a NEW camera object).
		holder.cam.position.copy(desired.position);
		holder.cam.quaternion.copy(desired.quaternion);
		holder.cam.up.copy(desired.up);
		holder.cam.fov = desired.fov;
		holder.cam.near = desired.near;
		holder.cam.far = desired.far;
		return holder.cam;
	})();
	// No usable viewpoint → disable the mode effectively (fall back to 2D) + let the hint render.
	const view3DActive = view3DEnabled && view3DCamera != null;
	const view3DNoViewpoint = view3DEnabled && view3DCamera == null;
	// Set15 P8 — resolve the shadow knobs ONCE here: both the `<Canvas shadows>` prop (which r3f
	// re-applies to `gl.shadowMap.enabled` on every re-render) and `Map3D_SceneLights` (which owns
	// the cast-shadow light) must agree, or one silently clobbers the other.
	const shadowSettings = {
		enabled: resolveShadowsEnabled(view3D.view3D_shadowsEnabled),
		mapSize: resolveShadowMapSize(view3D.view3D_shadowMapSize),
		radius: resolveShadowRadius(view3D.view3D_shadowRadius),
		bias: resolveShadowBias(view3D.view3D_shadowBias),
	};
	const shadowsActive = view3DActive && shadowSettings.enabled;

	return (
		<div style={{
			flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
			//background: "rgba(0, 0, 0, 0.5)", //backdropFilter: "blur(3px)",
		}}>
			<Row id="threejs-container"
				ref={c=>{
					const dom = c?.root;
					if (dom) RunInAction("MapSurface.3JSContainer.ref", ()=>uiState.mapSurface_3JSContainer = dom);
					return ()=>{
						if (dom && uiState.mapSurface_3JSContainer == dom) {
							RunInAction("MapSurface.3JSContainer.ref", ()=>uiState.mapSurface_3JSContainer = null);
						}
					};
				}}
				tabIndex={-1} // so event-handlers on this element can receive keyboard-events (see MapControls.ts)
				style={{
					width: "100%", height: "100%",
					touchAction: "none",
					// ensure elements rendered by store.main.maps.tools.ActiveTool().RenderInSceneOverlay() are clipped to this area
					// (some tools, like Measure, render position:fixed elements, which might otherwise end up displaying over eg. the right-edge panel)
					// (this clip-path also has the beneficial side-effect [not sure why] of preventing those elements from rendering "over" the toolbox buttons)
					clipPath: "inset(0px)",
				}}
			>
				{/* ⚠ `shadows` MUST be passed here, not only set imperatively in `Map3D_SceneLights`.
				    R3F's `configure()` runs `gl.shadowMap.enabled = !!shadows` on EVERY Canvas
				    re-render (that block is NOT behind its `if (!configured)` one-time guard). With
				    the prop absent, `shadows` is undefined → r3f reset `gl.shadowMap.enabled` back
				    to false on the next re-render after `Map3D_SceneLights`'s effect enabled it, so
				    three never rendered the depth maps and ALL 3D shadows silently vanished (the
				    tell-tale live state was `enabled === false` while `type === PCFSoftShadowMap`,
				    i.e. our shadows-on branch HAD run and then been clobbered).
				    "soft" == PCFSoftShadowMap, matching what that branch sets. Driven by the SAME
				    resolved setting the lights component gets, so toggling shadows off still tears the
				    shadow map down (the documented "leaked enabled → trees vanish" bug). */}
				<Canvas key={view3DActive ? "3d" : "2d"} ref={canvasRef}
					shadows={shadowsActive ? "soft" : false} camera={view3DActive ? view3DCamera! : self.camera}
					frameloop={mapState.settings.general.forceContinuousRendering ? "always" : "demand"}
					gl={{
						// Set5 FixDepthBuffers (2026-08-23) — logarithmic depth DISABLED. The 3D scene mixes
						// three's built-in materials (MeshBasicMaterial terrain bake, MeshStandardMaterial
						// trees) — which three auto-injects log-depth into — with CUSTOM GLSL3
						// ShaderMaterials (the terrain shader path + building surface materials) that write
						// STANDARD (hyperbolic) depth. Under `logarithmicDepthBuffer: true` the two depth
						// spaces are INCOMPARABLE: the log-depth terrain near the camera lands at
						// depth ≈ 0.16 while the standard-depth building lands at depth ≈ 0.98 (far:near =
						// 100000:0.1 destroys precision), so the geometrically-BEHIND terrain always wins the
						// LESS-depth test and renders IN FRONT of the buildings. Fix = use ONE depth space
						// everywhere (standard). See Docs/Plans/Terrain/Fixes/Set5_FixDepthBuffers.md.
						logarithmicDepthBuffer: false,
						// Set15 P5 debug — keep the drawing buffer after each frame so the agent can
						// read actual rendered pixels (default off; a perf-neutral debug aid).
						preserveDrawingBuffer: mapState.settings.general.debug_preserveDrawingBuffer === true,
					}}>
					<TweenUpdater self={self}/>
					{/* Set15 P5 — 3D only: move the live PerspectiveCamera in-place to the current
					    viewpoint eye (terrain height + eyeHeight) — NO Canvas remount (a remount would
					    re-solve the whole terrain pipeline just to move the camera). */}
					{view3DActive && <ViewpointCameraUpdater {...{map, mapState}}/>}
					<StatsCollector map={map} mapObjects={mapObjects}/>
					<ToneMappingController mapState={mapState}/>
					{/* Set15 P7 — the shared fixed ambient no longer suffices: the 3D NEAR-tier real
					    meshes (MeshStandardMaterial) need the terrain light rig. In 3D mode we install
					    `Map3D_SceneLights` (directional + ambient derived from the shading config — the
					    SAME rig the bake pre-lit the impostor with); 2D keeps the plain ambient (its
					    consumers — terrain + impostors — are all pre-lit). */}
					{view3DActive ? <Map3D_SceneLights mapObjects={mapObjects} shadowSettings={shadowSettings}/> : <ambientLight intensity={1}/>}
					{view3DActive ? (
						<>
							{/* §5 allow-list: terrain + scenery + sky/fog + per-object 3D renderers
							    (`MapObject3D` — the 3D counterpart of `MapObjectUI`). NOTHING else. */}
							<Map3D_SkyFog maxViewDistanceInput={view3D.maxViewDistance} fogDensityInput={view3D.fogDensity}/>
							<TerrainRenderer map={map} mapState={mapState}/>
							{mapState.settings.terrain.showScenery && <SceneryRenderer mapID={map.id} view3DSettings={view3D}/>}
							{/* Buildings Set1 §6.1 — per-object 3D renderers (geometry is P3+). */}
							{mapObjects.filter(ObjHasRenderableBuilding).map(obj=>
								<MapObject3D key={obj.id} obj={obj} map={map} mapState={mapState}/>)}
						</>
					) : (
						<>
							{/* 2D authoring surface — unchanged. */}
							<TerrainRenderer map={map} mapState={mapState}/>
							{mapState.settings.terrain.showScenery && <SceneryRenderer mapID={map.id}/>}
							<IsolineSamplePointsOverlay map={map} mapState={mapState}/>
							<CursorMarker mapState={mapState}/>
							<FocusAreaMarker mapState={mapState} focusLevel={1}/>
							<ViewpointOverlay map={map} mapState={mapState} objects={mapObjects}/>
							{(()=>{
								const Tool_RenderInScene_AsFuncComp = uiState.tools.ActiveTool()?.RenderInScene;
								if (!Tool_RenderInScene_AsFuncComp) return null;
								return <Tool_RenderInScene_AsFuncComp data={{map, mapState}}/>;
							})()}
							{mapObjects.map(obj=>{
								if (IsMapObjectHidden(obj, mapObjects)) return null;
								return <MapObjectUI /*ref={c=>this.mapObjectUIs[obj.id] = c}*/ key={obj.id} {...{obj, map, mapState}} />;
							})}
							<MapControls_React controlsRef={c=>self.mapControls = c} options={mapControlsOptions}/>
						</>
					)}
				</Canvas>
				{(()=>{
					// §5 — tools are hard-gated in 3D (their `GetWorldPosFromClientPos` assumes ortho).
					// In 2D, render the active tool's HTML overlay as before.
					if (view3DActive) return null;
					const Tool_RenderInSceneOverlay_AsFuncComp = uiState.tools.ActiveTool()?.RenderInSceneOverlay;
					if (!Tool_RenderInSceneOverlay_AsFuncComp) return null;
					return <Tool_RenderInSceneOverlay_AsFuncComp data={{map, mapState}}/>;
				})()}
				{/* Set15 P1 — a hint when 3D is toggled on but no usable viewpoint is selected.
				    Overlays the (2D-frozen) surface. */}
				{view3DNoViewpoint && (
					<div style={{
						position: "absolute", left: 0, top: 0, width: "100%", height: "100%",
						display: "flex", alignItems: "center", justifyContent: "center",
						pointerEvents: "none", zIndex: 100, background: "rgba(0,0,0,0.4)", color: "white",
						fontSize: 20, textAlign: "center",
					}}>
						<div>
							{viewpointObjects.length == 0
								? <>
									3D mode needs a Viewpoint.<br/>
									Add a "Viewpoint" modifier to a MapObject in the outliner to place one.
								</>
								: <>
									3D mode needs a Viewpoint.<br/>
									Select one of the {viewpointObjects.length} viewpoint objects on this map in
									the outliner, then toggle 3D on again (or pick it in Settings → 3D mode).
								</>}
						</div>
					</div>
				)}
				<MapSurface_MenuStub {...{map, mapState}}/>
			</Row>
		</div>
	);
});

const TweenUpdater = (props: {self: MapSurface_SelfState})=>{
	const {self} = props;
	// `frameloop="demand"` only runs `useFrame` when a frame is ALREADY rendering, so a freshly-
	// started tween is invisible unless it requests its own first frame. Store the live `invalidate`
	// fn on `self` so `FocusWorldPos` can kick that off; then keep invalidating while any tween plays.
	const {invalidate} = useThree();
	useEffect(()=>{
		self.invalidate = invalidate;
		return ()=>{ self.invalidate = null; };
	}, [invalidate, self]);
	useFrame(()=>{
		self.tweenGroup.update();
		const anyActive = self.tweenGroup.getAll().some(tween=>tween.isPlaying());
		if (anyActive) Invalidate3JS(invalidate, "mapControls.focusLerp", "focus/pan tween still animating");
	});
	return null;
};

/** Set15 P5 — 3D-only in-Canvas: keeps the ACTIVE PerspectiveCamera in sync with the CURRENT
 *  viewpoint (the `<Canvas>` camera prop is read only at mount).  Eye height is the BAKED absolute
 *  `eyeHeight` (stable, no terrain dep); re-applies only when viewpoint orientation/settings change. */
const ViewpointCameraUpdater = observer_mgl_r3((props: {map: DMap, mapState: MapState})=>{
	const {map} = props;
	const {camera, size} = useThree();
	// React to the active viewpoint / its modifier / the settings (yaw/pitch/fov/eyeHeight change).
	const view3D = props.mapState.settings.view3D;
	if (!view3D.view3D_enabled) return null;

	useFrame(()=>{
		const cam = camera as THREE.PerspectiveCamera;
		if (!cam || cam.isPerspectiveCamera !== true) return;
		// Recompute the desired camera from the baked eye height + orientation, copy onto live camera.
		const desired = resolveViewpointCamera(map.id, view3D.view3D_activeViewpointObjID);
		if (!desired) return;
		cam.position.copy(desired.position);
		cam.quaternion.copy(desired.quaternion);
		cam.up.copy(desired.up);
		cam.fov = desired.fov;
		cam.near = desired.near;
		cam.far = desired.far;
		// Set15 fix — keep the LIVE camera aspect locked to the canvas size (covers window resizes;
		// the memoized mount camera already carries it, but a resize must re-apply the new ratio).
		if (size.width > 0 && size.height > 0) {
			cam.aspect = size.width / size.height;
		}
		cam.updateProjectionMatrix();
	});
	return null;
});

/** Set14 P7 §4.11 — apply the MapSettings tone-mapping setting to the LIVE renderer each frame.
 *  three's `toneMapping` + `outputColorSpace` are RENDERER state that the terrain fragment (via
 *  the assembled `#ifdef TONE_MAPPING` + `linearToOutputTexel` transfer) and the scenery bake
 *  (via `MeshStandardMaterial`'s built-in includes + the bake signature) both read live — so this
 *  single write routes the setting to BOTH the terrain and the trees (the bake captures it per
 *  bake, and a change re-bakes because toneMapping joins the atlas signature).
 *
 *  Under `NoToneMapping` we ALSO pin `outputColorSpace` to LINEAR — because `linearToOutputTexel`
 *  is only the identity under a linear output space, so `NoToneMapping` + linear output is exactly
 *  the pre-P7 look (byte-identical). Under any non-zero mode the default SRGB output is kept
 *  (the sRGB encode is what gives the terrain its intended display curve).<｜end▁of▁thinking｜>
 *  This is a render-body/useFrame application rather than an R3F `gl` prop because the setting
 *  can change at runtime and the R3F canvas owns the renderer. */
const ToneMappingController = observer_mgl((props: {mapState: MapState})=>{
	const {gl} = useThree();
	const toneMapping = props.mapState.settings.terrain.toneMapping;
	useFrame(()=>{
		gl.toneMapping = toneMapping as THREE.ToneMapping;
		// NoToneMapping → linear output so the sRGB encode is the identity (exact pre-P7 look).
		gl.outputColorSpace = toneMapping == 0 ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace;
		gl.toneMappingExposure = 1.0;
	});
	return null;
});

/** Brackets the Three.js render call via scene callbacks (no useFrame priority needed) to measure render time; collects all stats post-render. */
const StatsCollector = ({map, mapObjects}: {map: DMap, mapObjects: MapObject[]})=>{

	const {gl, scene} = useThree();
	useEffect(()=>{
		const prevBefore = scene.onBeforeRender;
		const prevAfter = scene.onAfterRender;
		scene.onBeforeRender = function(...args) { prevBefore.apply(this, args); markRenderStart(); };
		scene.onAfterRender = function(...args) { prevAfter.apply(this, args); markRenderEnd(); };
		return ()=>{
			scene.onBeforeRender = prevBefore;
			scene.onAfterRender = prevAfter;
		};
	}, [scene]);
	
	useFrame(()=>{
		collectRenderStats(gl, id=>{
			//return mapObjects.find(o=>o.id == id)?.name ?? id;
			// faster, since backed by a Map<id, obj>.get() instead of an arr.find()
			return GetMapObject(id, map.id)?.name ?? id;
		});
	}, -100);
	return null;
};